import { useEffect, useRef, useState } from 'react'
import styles from './AudiologyFileInput.module.scss'

export const AUDIOGRAM_ACCEPT = '.pdf,.png,.jpg,.jpeg,.heic,.heif,application/pdf,image/png,image/jpeg,image/heic,image/heif'
export const AUDIOGRAM_MAX_BYTES = 15 * 1024 * 1024
const ALLOWED_EXT = ['pdf', 'png', 'jpg', 'jpeg', 'heic', 'heif']

export const validateUploadFile = (file: File, label = 'File'): string | null => {
    const ext = file.name.split('.').pop()?.toLowerCase() || ''
    if (!ALLOWED_EXT.includes(ext)) return `${label} must be a PDF, PNG, JPEG or HEIC file`
    if (file.size === 0) return 'The selected file is empty'
    if (file.size > AUDIOGRAM_MAX_BYTES) return `${label} must be 15 MB or smaller`
    return null
}

export const validateAudiogramFile = (file: File): string | null => validateUploadFile(file, 'Audiogram')

const formatBytes = (bytes: number) =>
    bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`

interface Props {
    file: File | null
    onChange: (file: File | null) => void
    error?: string | null
    required?: boolean
    disabled?: boolean
    id?: string
    label?: string
    hint?: string
    emptyTitle?: string
    emptySub?: string
    previewAlt?: string
}

/**
 * Drop-zone style picker for the client's audiogram (or any KYC file).
 * Shows an inline preview for PNG/JPEG, and a file card for PDF/HEIC.
 */
export const AudiologyFileInput = ({
    file,
    onChange,
    error,
    required,
    disabled,
    id = 'mhl-field-audiogram',
    label = 'Audiogram upload',
    hint = 'PDF, PNG, JPEG or HEIC · max 15 MB',
    emptyTitle = 'Click to choose the audiogram, or drop it here',
    emptySub = "A scan or photo of the client's audiogram report",
    previewAlt = 'File preview',
}: Props) => {
    const inputRef = useRef<HTMLInputElement | null>(null)
    const [dragging, setDragging] = useState(false)
    const [localError, setLocalError] = useState<string | null>(null)
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)

    useEffect(() => {
        if (!file || !/^image\/(png|jpe?g)$/i.test(file.type)) {
            setPreviewUrl(null)
            return
        }
        const url = URL.createObjectURL(file)
        setPreviewUrl(url)
        return () => URL.revokeObjectURL(url)
    }, [file])

    const accept = (candidate: File | undefined) => {
        if (!candidate) return
        const problem = validateUploadFile(candidate, label.replace(/\s+upload$/i, ''))
        setLocalError(problem)
        if (problem) return
        onChange(candidate)
    }

    const shownError = error || localError

    return (
        <div className={styles.field}>
            <span className={styles.label}>
                <span>
                    {label}{required && <span className={styles.req}>*</span>}
                </span>
                <span className={styles.labelHint}>{hint}</span>
            </span>

            <input
                ref={inputRef}
                id={id}
                type="file"
                accept={AUDIOGRAM_ACCEPT}
                className={styles.hidden}
                disabled={disabled}
                onChange={(e) => {
                    accept(e.target.files?.[0])
                    e.target.value = ''
                }}
            />

            {!file ? (
                <button
                    type="button"
                    className={`${styles.dropzone} ${dragging ? styles.dragging : ''} ${shownError ? styles.dropzoneError : ''}`}
                    disabled={disabled}
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => {
                        e.preventDefault()
                        if (!disabled) setDragging(true)
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={(e) => {
                        e.preventDefault()
                        setDragging(false)
                        if (!disabled) accept(e.dataTransfer.files?.[0])
                    }}
                >
                    <span className={styles.dropIcon} aria-hidden="true">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 16V4m0 0-4 4m4-4 4 4" />
                            <path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
                        </svg>
                    </span>
                    <span className={styles.dropTitle}>{emptyTitle}</span>
                    <span className={styles.dropSub}>{emptySub}</span>
                </button>
            ) : (
                <div className={styles.fileCard}>
                    {previewUrl ? (
                        <img src={previewUrl} alt={previewAlt} className={styles.thumb} />
                    ) : (
                        <span className={styles.fileBadge}>{file.name.split('.').pop()?.toUpperCase()}</span>
                    )}
                    <div className={styles.fileMeta}>
                        <span className={styles.fileName}>{file.name}</span>
                        <span className={styles.fileSize}>{formatBytes(file.size)}</span>
                    </div>
                    <div className={styles.fileActions}>
                        <button type="button" className={styles.linkBtn} disabled={disabled} onClick={() => inputRef.current?.click()}>
                            Replace
                        </button>
                        <button
                            type="button"
                            className={`${styles.linkBtn} ${styles.linkDanger}`}
                            disabled={disabled}
                            onClick={() => {
                                setLocalError(null)
                                onChange(null)
                            }}
                        >
                            Remove
                        </button>
                    </div>
                </div>
            )}

            {shownError && <span className={styles.error}>{shownError}</span>}
        </div>
    )
}
